import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

// App-wide, user-selectable style presets. Each is persisted to localStorage and drives the
// theme (header/button/tab styling) plus per-grid pagination rendering.
export const PAGINATION_STYLE = {
  RANGE: 'range',
  NUMBERED: 'numbered',
} as const;
export type PaginationStyle = typeof PAGINATION_STYLE[keyof typeof PAGINATION_STYLE];

export const HEADER_STYLE = {
  WHITE_BOLD: 'whiteBold',
  GRAY_BOLD: 'grayBold',
  GRAY_REGULAR: 'grayRegular',
} as const;
export type HeaderStyle = typeof HEADER_STYLE[keyof typeof HEADER_STYLE];

export const BUTTON_CASING = {
  UPPERCASE: 'uppercase',
  TITLE: 'title',
} as const;
export type ButtonCasing = typeof BUTTON_CASING[keyof typeof BUTTON_CASING];

export const TAB_CASING = {
  TITLE: 'title',
  UPPERCASE: 'uppercase',
} as const;
export type TabCasing = typeof TAB_CASING[keyof typeof TAB_CASING];

export interface StylePreferences {
  paginationStyle: PaginationStyle;
  headerStyle: HeaderStyle;
  buttonCasing: ButtonCasing;
  tabCasing: TabCasing;
}

// Default style presets applied when the user hasn't chosen otherwise.
export const DEFAULT_STYLE_PREFERENCES: StylePreferences = {
  paginationStyle: PAGINATION_STYLE.NUMBERED,
  headerStyle: HEADER_STYLE.GRAY_BOLD,
  buttonCasing: BUTTON_CASING.UPPERCASE,
  tabCasing: TAB_CASING.UPPERCASE,
};

const STORAGE_KEY = 'creditpulse.style-preferences';

interface StylePreferencesContextValue {
  preferences: StylePreferences;
  setPreference: <K extends keyof StylePreferences>(key: K, value: StylePreferences[K]) => void;
}

const StylePreferencesContext = createContext<StylePreferencesContextValue>({
  preferences: DEFAULT_STYLE_PREFERENCES,
  setPreference: () => {},
});

// Validate each stored value against its allowed set so a partial or stale localStorage entry
// can never inject an unknown value into the theme.
const isAllowedValue = <T extends string>(allowed: Record<string, T>, value: unknown): value is T =>
  typeof value === 'string' && (Object.values(allowed) as string[]).includes(value);

const readStoredPreferences = (): StylePreferences => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    const stored: Partial<Record<keyof StylePreferences, unknown>> =
      typeof parsed === 'object' && parsed !== null
        ? (parsed as Partial<Record<keyof StylePreferences, unknown>>)
        : {};
    return {
      paginationStyle: isAllowedValue(PAGINATION_STYLE, stored.paginationStyle)
        ? stored.paginationStyle
        : DEFAULT_STYLE_PREFERENCES.paginationStyle,
      headerStyle: isAllowedValue(HEADER_STYLE, stored.headerStyle)
        ? stored.headerStyle
        : DEFAULT_STYLE_PREFERENCES.headerStyle,
      buttonCasing: isAllowedValue(BUTTON_CASING, stored.buttonCasing)
        ? stored.buttonCasing
        : DEFAULT_STYLE_PREFERENCES.buttonCasing,
      tabCasing: isAllowedValue(TAB_CASING, stored.tabCasing)
        ? stored.tabCasing
        : DEFAULT_STYLE_PREFERENCES.tabCasing,
    };
  } catch {
    return DEFAULT_STYLE_PREFERENCES;
  }
};

export const StylePreferencesProvider = ({ children }: { children: React.ReactNode }) => {
  const [ preferences, setPreferences ] = useState<StylePreferences>(readStoredPreferences);

  const setPreference = useCallback(
    <K extends keyof StylePreferences>(key: K, value: StylePreferences[K]) => {
      setPreferences((prev) => {
        const next = {
          ...prev,
          [key]: value,
        };
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
          // Ignore storage failures (e.g. private mode); the choice still applies in-memory.
        }
        return next;
      });
    },
    [],
  );

  const value = useMemo((): StylePreferencesContextValue => ({
    preferences,
    setPreference,
  }), [ preferences, setPreference ]);

  return (
    <StylePreferencesContext.Provider value={value}>
      {children}
    </StylePreferencesContext.Provider>
  );
};

export const useStylePreferences = () => useContext(StylePreferencesContext);
